import { tap } from 'rxjs/operators';
import { Subscription } from 'rxjs/Subscription';
import { Tag } from '../../../../interfaces/tag';
import { Toast, ToastsManager } from 'ng2-toastr';
import { Status } from '../../../../interfaces/status';
import { FormControl, FormGroup } from '@angular/forms';
import { DataHelper } from '../../../../helpers/data-helper';
import { OfferIface } from '../../../../interfaces/offer.iface';
import { AuthService } from '../../../../services/auth.service';
import { OffersService } from '../../../../services/offers.service';
import { LoggerService } from '../../../../services/logger.service';
import { StatusService } from '../../../../services/status.service';
import { RESTDataSource } from '../../../../helpers/rest.datasource';
import { MatChipInputEvent, MatDialog, MatPaginator } from '@angular/material';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ConfirmOffersEditDialogComponent } from './confirm-offers-edit-dialog/confirm-offers-edit-dialog.component';
import { MassOffersEditParamsIface } from '../../../../interfaces/massOffersEditParams.iface';
import { TagsServiceTypeEnum } from '../../../../enums/tags-service-type.enum';

@Component({
  selector: 'app-mass-offers-edit',
  templateUrl: './mass-offers-edit.component.html',
  styleUrls: ['./mass-offers-edit.component.scss']
})
export class MassOffersEditComponent implements OnInit, OnDestroy, AfterViewInit {
  private static readonly MASS_SELECT_ALL_OFFERS = 'WSZYSTKIE';
  @ViewChild(MatPaginator)
  paginator: MatPaginator;
  public readonly tagsServiceTypeEnum = TagsServiceTypeEnum;
  public displayedColumns = [
    'action',
    'availability',
    'availableInLocations',
    'availableInChannels',
    'availableForCustomerSegments',
    'code',
    'price',
    'period'
  ];
  public defaultSelect: string;
  public isLoadingResults = false;
  public statuses: Status[];
  public selectedStatus: Status;
  public statusToEdit: Status[];
  public activeTags: Tag[] = [];
  public customTags: Tag[] = [];
  public massEditForm: FormGroup;
  public subscription: Subscription;
  public chosenOffers: OfferIface[] = [];
  public serviceName = OffersService.name;
  public dataSource: RESTDataSource<OfferIface>;
  public statusesToSelect: string[] = [MassOffersEditComponent.MASS_SELECT_ALL_OFFERS];

  constructor(
    private dialog: MatDialog,
    private toastr: ToastsManager,
    private authService: AuthService,
    private offersService: OffersService,
    private statusService: StatusService,
    private route: ActivatedRoute
  ) {
    this.massEditForm = new FormGroup(
      {
        code: new FormControl(''),
        offerStatus: new FormControl(''),
        availableSince: new FormControl(''),
        availableUntil: new FormControl(''),
        linkToTermsOfService: new FormControl(''),
        comments: new FormControl('')
      },
      DataHelper.AtLeastOneFieldValidator
    );
  }

  ngOnInit() {
    this.dataSource = new RESTDataSource(this.offersService);
    this.subscription = this.dataSource.loading$.subscribe(state => {
      this.isLoadingResults = state;
    });
    this.paginator.pageSize = 10;
    this.statusService.getStatusesWithEditPermForRoles(this.authService.user.roles).subscribe(statuses => {
      this.statusToEdit = statuses;
      this.statuses = statuses.filter(status => status.statusCode !== 'withdrawn');
      this.getStatusesToSelect();
      this.loadData();
    });
  }

  ngAfterViewInit() {
    LoggerService.log('CustomerSegmentEditComponent::ngAfterViewInit');
    this.paginator.page.pipe(tap(() => this.loadData())).subscribe();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  getStatusesToSelect(): void {
    this.statuses.forEach(status => this.statusesToSelect.push(status.statusName));
    this.readParamAndSetStatus();
  }

  readParamAndSetStatus() {
    this.route.queryParams.subscribe(params => {
      const chosenStatus: string = params.status ? params.status.toUpperCase() : '';
      this.defaultSelect = this.statusesToSelect.find(status => status === chosenStatus);
      if (!this.defaultSelect) {
        return (this.defaultSelect = this.statusesToSelect[0]);
      }
      this.selectedStatus = this.statuses.find(status => status.statusName === chosenStatus);
    });
  }

  loadData(): void {
    let query = {
      _id: { $nin: this.chosenOffers.map(o => o._id) }
    };
    const activeTags = this.activeTags.map(activeTag => activeTag.value);
    if (activeTags.length) {
      query = Object.assign(query, { 'tags.value': { $all: activeTags } });
    }
    query = Object.assign(query, {
      offerStatus: !this.selectedStatus
        ? { $in: this.statuses.map(status => status.statusCode) }
        : this.selectedStatus.statusCode
    });
    this.dataSource.loadData(query, this.paginator.pageIndex, this.paginator.pageSize, '');
  }

  chooseOffer(offer: OfferIface): Promise<Toast> {
    const exists = this.chosenOffers.some(o => o._id === offer._id);
    if (exists) {
      return this.toastr.info('Ta oferta jest już dodana do listy');
    }
    this.chosenOffers.push(offer);
    this.loadData();
  }

  filterByTags(tags: Tag[]): void {
    this.paginator.firstPage();
    this.activeTags = tags;
    this.loadData();
  }

  deleteOffer(index: number): void {
    this.chosenOffers.splice(index, 1);
    this.loadData();
  }

  onSelectedStatusChange(event): void {
    const selectedStatusIndex = this.statuses.findIndex(idx => idx.statusName === event.value);
    this.selectedStatus = this.statuses[selectedStatusIndex];
    this.loadData();
  }

  chooseAllOffers(): void {
    const activeTags = this.activeTags.map(activeTag => activeTag.value);
    const query = { 'tags.value': { $all: activeTags } };
    this.offersService.findAll(query, 0, 0).subscribe(offers => {
      this.chosenOffers = [
        ...this.chosenOffers,
        ...offers.filter(offer => {
          return this.chosenOffers.every(element => {
            return element._id !== offer._id;
          });
        })
      ];
      this.loadData();
    });
  }

  addCustomTag(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;

    if ((value || '').trim()) {
      this.customTags.push({ category: 'custom', value: value.trim() });
    }

    if (input) {
      input.value = '';
    }
  }

  removeCustomTag(customTag: Tag): void {
    const index = this.customTags.map(tag => tag.value).indexOf(customTag.value);

    if (index >= 0) {
      this.customTags.splice(index, 1);
    }
  }

  saveMassOffers(massEditForm: FormGroup): Promise<Toast> {
    if (!this.chosenOffers.length || (this.massEditForm.invalid && !this.customTags.length)) {
      return this.toastr.info(
        'Aby można było zapisać zmiany, wybierz oferty do edycji oraz przynajmniej jedno pole które chcesz zmienić.',
        'Uwaga'
      );
    }
    this.dialog
      .open(ConfirmOffersEditDialogComponent, {
        panelClass: 'confirm-offers-edit-dialog',
        disableClose: true,
        data: {
          offers: this.chosenOffers.length
        }
      })
      .afterClosed()
      .subscribe(res => {
        if (res) {
          const query: MassOffersEditParamsIface = this.createQuery(massEditForm);
          this.offersService.massOffersUpdate(query).subscribe(
            () => {
              this.toastr.success('Zaktualizowano poprawnie oferty', 'Sukces');
              this.customTags = [];
              this.activeTags = [];
              this.chosenOffers = [];
              this.massEditForm.reset();
              this.isLoadingResults = false;
              this.loadData();
            },
            () => {
              this.isLoadingResults = false;
              this.toastr.error('Problem z aktualizacją ofert', 'Błąd');
            }
          );
        }
      });
  }

  createQuery(massEditForm: FormGroup): MassOffersEditParamsIface {
    const setOperator = { $set: {} };
    const pushOperator = { $push: {} };
    Object.assign(setOperator.$set, { updatedAt: Date.now(), updatedBy: this.authService.user.username });

    if (massEditForm.value.code) {
      Object.assign(setOperator.$set, { code: massEditForm.value.code });
    }
    if (massEditForm.value.linkToTermsOfService) {
      Object.assign(setOperator.$set, { linkToTermsOfService: massEditForm.value.linkToTermsOfService });
    }
    if (massEditForm.value.availableSince) {
      Object.assign(setOperator.$set, { availableSince: new Date(massEditForm.value.availableSince).getTime() });
    }
    if (massEditForm.value.availableUntil) {
      Object.assign(setOperator.$set, { availableUntil: new Date(massEditForm.value.availableUntil).getTime() });
    }
    if (massEditForm.value.offerStatus) {
      Object.assign(setOperator.$set, { offerStatus: massEditForm.value.offerStatus });
    }
    if (this.customTags.length) {
      Object.assign(pushOperator.$push, { tags: this.customTags });
    }
    if (massEditForm.value.comments) {
      Object.assign(pushOperator.$push, {
        comments: {
          content: massEditForm.value.comments,
          author: this.authService.user.username,
          date: Date.now(),
          isNew: true
        }
      });
    }
    const filter = { $and: [{ _id: { $in: this.chosenOffers.map(o => o._id) } }, { 'tags.category': 'offerStatus' }] };
    const addSetOperator = Object.keys(setOperator.$set).length !== 0 ? setOperator : null;
    const addPushOperator = Object.keys(pushOperator.$push).length !== 0 ? pushOperator : null;
    const updateOperators = Object.assign({}, addSetOperator, addPushOperator);
    return {
      offersFilter: filter,
      offersQuery: updateOperators,
      statusValue: massEditForm.value.offerStatus
    };
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/offers/offer-list/mass-offers-edit/mass-offers-edit.component.ts